import { useState } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Drawer from "@material-ui/core/Drawer";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import AddIcon from "@material-ui/icons/AddRounded";
import DoneIcon from "@material-ui/icons/Done";
import { DeleteOutlined } from "@material-ui/icons";

import { ReportItem, ItemGroup, SuggestionBasedText } from "pages/photo/types";
import { useMaterials } from "features/firebase/materials/MaterialsProvider";
import BrandPicker from "../BrandPicker";
import ClickableTooltip from "components/common/ClickableTooltip";

const useStyles = makeStyles((theme) => ({
  drawer: {
    borderTopLeftRadius: theme.spacing(2),
    borderTopRightRadius: theme.spacing(2),
    maxHeight: "85vh"
  },
  container: {
    padding: theme.spacing(1.6),
    display: "flex",
    flexDirection: "column",
    maxHeight: "85vh",
    overflowY: "auto"
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2)
  },
  title: {
    fontWeight: 700,
    fontSize: "1.5rem",
    color: "#0E2E3A"
  },
  subtitle: {
    fontSize: "0.8125rem",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2)
  },
  splitHeader: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1)
  },
  splitInstruction: {
    fontSize: "0.8125rem",
    color: theme.palette.text.secondary
  },
  groupRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5)
  },
  selectControl: {
    flex: 1,
    minWidth: 0
  },
  select: {
    backgroundColor: "#FFFFFF",
    borderColor: "#D0DBDF",
    borderRadius: "4px",
    color: "#0E2E3A",
    fontSize: "1rem",
    padding: `${theme.spacing(1)}px ${theme.spacing(0.8)}px`,
    "& .MuiSelect-select": {
      padding: "0 !important"
    },
    "& .MuiOutlinedInput-input": {
      padding: "0 !important"
    }
  },
  quantitySelectControl: {
    width: 60,
    flexShrink: 0
  },
  addTypeButton: {
    alignSelf: "flex-start",
    color: theme.palette.primary.main,
    fontSize: "1rem",
    fontWeight: 400,
    justifyContent: "flex-start",
    paddingLeft: 0,
    textDecoration: "underline",
    textTransform: "none",
    marginTop: theme.spacing(1)
  },
  assignedText: {
    alignSelf: "flex-start",
    color: "#495459",
    fontSize: "1rem",
    textAlign: "left",
    width: "100%"
  },
  saveButton: {
    marginTop: theme.spacing(2),
    textTransform: "none"
  },
  deleteButton: {
    color: "#495459",
    padding: 0
  }
}));

const emptyGroup = (): ItemGroup => ({
  brand: { label: null, id: null },
  material: { label: null, id: null },
  quantity: 1
});

type Props = {
  open: boolean;
  reportItem: ReportItem | null;
  onClose: () => void;
  onSave: (groups: ItemGroup[]) => void;
  onDelete: () => void;
};

export default function ItemDetailBottomSheet({
  open,
  reportItem,
  onClose,
  onSave,
  onDelete
}: Props) {
  const classes = useStyles();
  const materials = useMaterials();

  const [trackedItem, setTrackedItem] = useState<ReportItem | null>(reportItem);
  const [groups, setGroups] = useState<ItemGroup[]>(
    reportItem && reportItem.groups.length > 0
      ? reportItem.groups
      : [emptyGroup()]
  );

  // Derived state: reset groups whenever the item reference changes
  if (trackedItem !== reportItem) {
    setTrackedItem(reportItem);
    setGroups(
      reportItem && reportItem.groups.length > 0
        ? reportItem.groups
        : [emptyGroup()]
    );
  }

  const [brandPickerIndex, setBrandPickerIndex] = useState<number | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const assignedCount = groups.reduce((sum, g) => sum + g.quantity, 0);
  const totalQuantity = reportItem?.totalQuantity ?? 0;
  const hasReachedTotalQuantity = assignedCount >= totalQuantity;

  const getMaxQuantityForGroup = (index: number) => {
    const otherGroupsAssigned = groups.reduce((sum, group, i) => {
      if (i === index) {
        return sum;
      }

      return sum + group.quantity;
    }, 0);

    return Math.max(0, totalQuantity - otherGroupsAssigned);
  };

  const updateGroup = (index: number, updates: Partial<ItemGroup>) => {
    setGroups((prev) =>
      prev.map((g, i) => (i === index ? { ...g, ...updates } : g))
    );
  };

  const updateQuantity = (index: number, quantity: number) => {
    setGroups((prev) =>
      prev.flatMap((group, i) => {
        if (i !== index) {
          return [group];
        }

        if (quantity === 0) {
          return [];
        }

        return [{ ...group, quantity }];
      })
    );
  };

  const handleSave = () => {
    // Filter out groups with quantity 0 or no meaningful data
    const validGroups = groups.filter((g) => g.quantity > 0);
    onSave(validGroups);
  };

  const handleDeleteClick = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setIsDeleteDialogOpen(false);
  };

  const handleDeleteConfirm = () => {
    setIsDeleteDialogOpen(false);
    onDelete();
  };

  if (brandPickerIndex !== null) {
    return (
      <Drawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        PaperProps={{ className: classes.drawer }}
      >
        <BrandPicker
          onSelect={(brand: SuggestionBasedText) => {
            updateGroup(brandPickerIndex, { brand });
            setBrandPickerIndex(null);
          }}
          onBack={() => setBrandPickerIndex(null)}
        />
      </Drawer>
    );
  }

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{ className: classes.drawer }}
    >
      <div className={classes.container}>
        <div className={classes.header}>
          <Typography className={classes.title}>
            {reportItem?.category.label}
          </Typography>
          <IconButton
            aria-label="Delete item"
            className={classes.deleteButton}
            onClick={handleDeleteClick}
          >
            <DeleteOutlined />
          </IconButton>
        </div>

        <div className={classes.splitHeader}>
          <Typography className={classes.splitInstruction}>
            Enter information below
          </Typography>

          <ClickableTooltip
            label="Why do we need this?"
            title="Brand and material help us identify the biggest polluters and push for change"
            placement="bottom"
          />
        </div>

        {groups.map((group, index) => (
          <div className={classes.groupRow} key={index}>
            <FormControl
              variant="outlined"
              size="small"
              className={classes.selectControl}
            >
              <Select
                displayEmpty
                value={group.brand.label || ""}
                onClick={() => setBrandPickerIndex(index)}
                className={classes.select}
                inputProps={{ readOnly: true }}
              >
                <MenuItem value="" disabled>
                  Brand
                </MenuItem>
                {group.brand.label && (
                  <MenuItem value={group.brand.label}>
                    {group.brand.label}
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            <FormControl
              variant="outlined"
              size="small"
              className={classes.selectControl}
            >
              <Select
                displayEmpty
                value={group.material.id || ""}
                onChange={(e) => {
                  const materialId = e.target.value as string;
                  const mat = materials[materialId];
                  updateGroup(index, {
                    material: {
                      id: materialId,
                      label: mat?.label || null
                    }
                  });
                }}
                className={classes.select}
              >
                <MenuItem value="" disabled>
                  Material
                </MenuItem>
                {Object.entries(materials).map(([id, { label }]) => (
                  <MenuItem key={id} value={id}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              variant="outlined"
              size="small"
              className={classes.quantitySelectControl}
            >
              <Select
                value={group.quantity}
                onChange={(e) => updateQuantity(index, Number(e.target.value))}
                className={classes.select}
              >
                {Array.from(
                  {
                    length:
                      Math.max(group.quantity, getMaxQuantityForGroup(index)) +
                      1
                  },
                  (_, value) => value
                ).map((value) => (
                  <MenuItem key={value} value={value}>
                    {value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        ))}

        <Button
          startIcon={<AddIcon />}
          className={classes.addTypeButton}
          onClick={() => {
            if (hasReachedTotalQuantity) {
              return;
            }

            setGroups((prev) => [...prev, emptyGroup()]);
          }}
          disabled={hasReachedTotalQuantity}
        >
          Add another type
        </Button>

        <Typography className={classes.assignedText}>
          Assigned: {assignedCount} out of {totalQuantity} items
        </Typography>

        <Button
          variant="outlined"
          color="primary"
          size="large"
          fullWidth
          disableElevation
          className={classes.saveButton}
          onClick={handleSave}
          startIcon={<DoneIcon />}
        >
          Update
        </Button>

        <Dialog
          open={isDeleteDialogOpen}
          onClose={handleDeleteCancel}
          aria-labelledby="delete-item-dialog-title"
        >
          <DialogTitle id="delete-item-dialog-title">
            <span style={{ color: "#495459" }}>Delete this item?</span>
          </DialogTitle>
          <DialogContent>
            <Typography>
              This will remove it from your report. This action cannot be
              undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel} color="default">
              Cancel
            </Button>
            <Button onClick={handleDeleteConfirm} color="secondary" autoFocus>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </Drawer>
  );
}
